/**
 * Currency Flags System — Supabank Canon
 *
 * SSOT for fiat currency flags using react-circle-flags.
 * Maps ISO 4217 currency codes to ISO 3166-1 alpha-2 country codes.
 *
 * @see https://www.npmjs.com/package/react-circle-flags
 */

// Currency to country code mapping
export const FIAT_REGISTRY = {
  // Major world currencies
  usd: { name: 'US Dollar', symbol: '$', country: 'us', region: 'Americas' },
  eur: { name: 'Euro', symbol: '€', country: 'eu', region: 'Europe' },
  gbp: { name: 'British Pound', symbol: '£', country: 'gb', region: 'Europe' },
  jpy: { name: 'Japanese Yen', symbol: '¥', country: 'jp', region: 'Asia' },
  cny: { name: 'Chinese Yuan', symbol: '¥', country: 'cn', region: 'Asia' },
  chf: { name: 'Swiss Franc', symbol: 'Fr', country: 'ch', region: 'Europe' },
  cad: { name: 'Canadian Dollar', symbol: 'C$', country: 'ca', region: 'Americas' },
  aud: { name: 'Australian Dollar', symbol: 'A$', country: 'au', region: 'Oceania' },

  // Latin America (Supabank focus)
  brl: { name: 'Brazilian Real', symbol: 'R$', country: 'br', region: 'Americas' },
  mxn: { name: 'Mexican Peso', symbol: '$', country: 'mx', region: 'Americas' },
  ars: { name: 'Argentine Peso', symbol: '$', country: 'ar', region: 'Americas' },
  cop: { name: 'Colombian Peso', symbol: '$', country: 'co', region: 'Americas' },
  clp: { name: 'Chilean Peso', symbol: '$', country: 'cl', region: 'Americas' },
  pen: { name: 'Peruvian Sol', symbol: 'S/', country: 'pe', region: 'Americas' },
  uyu: { name: 'Uruguayan Peso', symbol: '$U', country: 'uy', region: 'Americas' },

  // Europe
  sek: { name: 'Swedish Krona', symbol: 'kr', country: 'se', region: 'Europe' },
  nok: { name: 'Norwegian Krone', symbol: 'kr', country: 'no', region: 'Europe' },
  dkk: { name: 'Danish Krone', symbol: 'kr', country: 'dk', region: 'Europe' },
  pln: { name: 'Polish Zloty', symbol: 'zł', country: 'pl', region: 'Europe' },
  czk: { name: 'Czech Koruna', symbol: 'Kč', country: 'cz', region: 'Europe' },
  huf: { name: 'Hungarian Forint', symbol: 'Ft', country: 'hu', region: 'Europe' },
  ron: { name: 'Romanian Leu', symbol: 'lei', country: 'ro', region: 'Europe' },
  bgn: { name: 'Bulgarian Lev', symbol: 'лв', country: 'bg', region: 'Europe' },

  // Asia
  inr: { name: 'Indian Rupee', symbol: '₹', country: 'in', region: 'Asia' },
  krw: { name: 'South Korean Won', symbol: '₩', country: 'kr', region: 'Asia' },
  sgd: { name: 'Singapore Dollar', symbol: 'S$', country: 'sg', region: 'Asia' },
  hkd: { name: 'Hong Kong Dollar', symbol: 'HK$', country: 'hk', region: 'Asia' },
  thb: { name: 'Thai Baht', symbol: '฿', country: 'th', region: 'Asia' },
  php: { name: 'Philippine Peso', symbol: '₱', country: 'ph', region: 'Asia' },
  myr: { name: 'Malaysian Ringgit', symbol: 'RM', country: 'my', region: 'Asia' },
  idr: { name: 'Indonesian Rupiah', symbol: 'Rp', country: 'id', region: 'Asia' },
  vnd: { name: 'Vietnamese Dong', symbol: '₫', country: 'vn', region: 'Asia' },

  // Middle East & Africa
  aed: { name: 'UAE Dirham', symbol: 'د.إ', country: 'ae', region: 'MiddleEast' },
  sar: { name: 'Saudi Riyal', symbol: '﷼', country: 'sa', region: 'MiddleEast' },
  ils: { name: 'Israeli Shekel', symbol: '₪', country: 'il', region: 'MiddleEast' },
  zar: { name: 'South African Rand', symbol: 'R', country: 'za', region: 'Africa' },
  ngn: { name: 'Nigerian Naira', symbol: '₦', country: 'ng', region: 'Africa' },
  egp: { name: 'Egyptian Pound', symbol: 'E£', country: 'eg', region: 'Africa' },

  // Other
  nzd: { name: 'New Zealand Dollar', symbol: 'NZ$', country: 'nz', region: 'Oceania' },
  rub: { name: 'Russian Ruble', symbol: '₽', country: 'ru', region: 'Europe' },
  try: { name: 'Turkish Lira', symbol: '₺', country: 'tr', region: 'Europe' },
} as const;

export type FiatCurrency = keyof typeof FIAT_REGISTRY;

/**
 * Get country code for flag rendering
 * @param currency - ISO 4217 currency code (lowercase)
 */
export function getCountryCode(currency: string): string | null {
  const normalizedCurrency = currency.toLowerCase() as FiatCurrency;
  return FIAT_REGISTRY[normalizedCurrency]?.country || null;
}

/**
 * Get fiat currency metadata
 */
export function getFiatMeta(currency: string): {
  name: string;
  symbol: string;
  country: string;
  region: string;
} | null {
  const normalizedCurrency = currency.toLowerCase() as FiatCurrency;
  return FIAT_REGISTRY[normalizedCurrency] || null;
}

/**
 * Check if currency is a known fiat
 */
export function isFiat(currency: string): boolean {
  return currency.toLowerCase() in FIAT_REGISTRY;
}

/**
 * Get all supported fiat currencies
 */
export function getSupportedFiats(): FiatCurrency[] {
  return Object.keys(FIAT_REGISTRY) as FiatCurrency[];
}

/**
 * Get fiats by region
 */
export function getFiatsByRegion(region: string): FiatCurrency[] {
  return (Object.entries(FIAT_REGISTRY) as [FiatCurrency, typeof FIAT_REGISTRY[FiatCurrency]][])
    .filter(([, meta]) => meta.region === region)
    .map(([currency]) => currency);
}

/**
 * Latin American currencies (Supabank primary market)
 */
export const LATAM_CURRENCIES = ['brl', 'mxn', 'ars', 'cop', 'clp', 'pen', 'uyu'] as const;

export function isLatamCurrency(currency: string): boolean {
  return LATAM_CURRENCIES.includes(currency.toLowerCase() as typeof LATAM_CURRENCIES[number]);
}
